import { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Newspaper, Search, Tag, ExternalLink, Clock, MapPin,
  ChevronLeft, ChevronRight, Loader2, Zap, ChevronDown,
  X, ArrowUpDown, SlidersHorizontal, Bookmark, BookmarkCheck, Trash2
} from 'lucide-react';
import PageLayout from '@/components/layout/PageLayout';
import { Link } from 'react-router-dom';
import ShareButtons from '@/components/ShareButtons';
import { SocialImage, newsCardSrc } from '@/components/SocialImage';

const API = process.env.REACT_APP_BACKEND_URL;

const REGION_FILTERS = ['Global', 'Europe', 'Middle East', 'Americas', 'Asia-Pacific', 'Africa'];
const MANDATE_FILTERS = ['eInvoicing', 'SAF-T', 'VAT', 'CTC', 'Pillar Two', 'Digital Reporting'];
const SORT_OPTIONS = [
  { value: 'newest', label: 'Newest first' },
  { value: 'oldest', label: 'Oldest first' },
  { value: 'relevance', label: 'Most relevant' },
];
const SAVED_FILTERS_KEY = 'taxera_saved_filters';

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch { return ''; }
};

/* ─── Multi-select helper ─── */
const toggleSet = (set, val) => {
  const next = new Set(set);
  if (next.has(val)) next.delete(val);
  else next.add(val);
  return next;
};

const NewsPage = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [regions, setRegions] = useState(new Set());
  const [countries, setCountries] = useState(new Set());
  const [mandateTypes, setMandateTypes] = useState(new Set());
  const [sources, setSources] = useState(new Set());
  const [sortBy, setSortBy] = useState('newest');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [countriesByRegion, setCountriesByRegion] = useState({});
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  const [showSavePanel, setShowSavePanel] = useState(false);
  const [savedFilters, setSavedFilters] = useState([]);
  const [saveFilterName, setSaveFilterName] = useState('');
  const sortRef = useRef(null);
  const savePanelRef = useRef(null);

  const hasActiveFilters = regions.size > 0 || countries.size > 0 || mandateTypes.size > 0 || sources.size > 0 || search;

  // Build query params for multi-select
  // eslint-disable-next-line react-hooks/exhaustive-deps -- API is a module constant
  const fetchArticles = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 20, sort_by: sortBy });
      if (regions.size > 0) params.set('region', [...regions].join(','));
      if (countries.size > 0) params.set('country', [...countries].join(','));
      if (mandateTypes.size > 0) params.set('mandate_type', [...mandateTypes].join(','));
      if (sources.size > 0) params.set('source', [...sources].join(','));
      if (search) params.set('search', search);
      const res = await fetch(`${API}/api/news/articles?${params}`);
      if (res.ok) {
        const data = await res.json();
        setArticles(data.articles || []);
        setTotalPages(data.pages || 1);
      }
    } catch (e) { console.error('Failed to fetch news articles:', e); }
    finally { setLoading(false); }
  }, [regions, countries, mandateTypes, sources, search, sortBy, page]);

  const fetchStats = async () => {
    try {
      const res = await fetch(`${API}/api/news/stats`);
      if (res.ok) setStats(await res.json());
    } catch (e) { console.error('Failed to fetch news stats:', e); }
  };

  const fetchCountries = async () => {
    try {
      const res = await fetch(`${API}/api/news/countries-by-region`);
      if (res.ok) setCountriesByRegion(await res.json());
    } catch (e) { console.error('Failed to fetch countries:', e); }
  };

  useEffect(() => { fetchArticles(); }, [fetchArticles]);
  useEffect(() => {
    fetchStats();
    fetchCountries();
    const stored = sessionStorage.getItem(SAVED_FILTERS_KEY);
    if (stored) setSavedFilters(JSON.parse(stored));
  }, []);

  // Close dropdowns on outside click
  useEffect(() => {
    const handler = (e) => {
      if (sortRef.current && !sortRef.current.contains(e.target)) setShowSortDropdown(false);
      if (savePanelRef.current && !savePanelRef.current.contains(e.target)) setShowSavePanel(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleSearch = (e) => { e.preventDefault(); setSearch(searchInput); setPage(1); };

  const clearFilters = () => {
    setRegions(new Set());
    setCountries(new Set());
    setMandateTypes(new Set());
    setSources(new Set());
    setSearch('');
    setSearchInput('');
    setSortBy('newest');
    setPage(1);
  };

  const toggleRegion = (r) => {
    const next = toggleSet(regions, r);
    setRegions(next);
    // Remove countries that are no longer under any selected region
    if (next.size === 0) {
      setCountries(new Set());
    } else {
      const validCountries = new Set();
      next.forEach(reg => {
        (countriesByRegion[reg] || []).forEach(c => validCountries.add(c));
      });
      setCountries(prev => {
        const cleaned = new Set([...prev].filter(c => validCountries.has(c)));
        return cleaned.size === prev.size ? prev : cleaned;
      });
    }
    setPage(1);
  };

  const toggleCountry = (c) => {
    setCountries(toggleSet(countries, c));
    setPage(1);
  };

  const toggleMandate = (m) => {
    setMandateTypes(toggleSet(mandateTypes, m));
    setPage(1);
  };

  const toggleSource = (s) => {
    setSources(toggleSet(sources, s));
    setPage(1);
  };

  // Merge countries for all selected regions
  const availableCountries = (() => {
    if (regions.size === 0) return [];
    const merged = new Set();
    regions.forEach(r => {
      (countriesByRegion[r] || []).forEach(c => merged.add(c));
    });
    return [...merged].sort();
  })();

  /* ─── Saved filters ─── */
  const saveCurrentFilter = () => {
    if (!saveFilterName.trim() || !hasActiveFilters) return;
    const filter = {
      id: Date.now().toString(),
      name: saveFilterName.trim(),
      regions: [...regions],
      countries: [...countries],
      mandateTypes: [...mandateTypes],
      search,
      sortBy,
    };
    const updated = [...savedFilters, filter];
    setSavedFilters(updated);
    sessionStorage.setItem(SAVED_FILTERS_KEY, JSON.stringify(updated));
    setSaveFilterName('');
    setShowSavePanel(false);
  };

  const loadSavedFilter = (filter) => {
    setRegions(new Set(filter.regions || []));
    setCountries(new Set(filter.countries || []));
    setMandateTypes(new Set(filter.mandateTypes || []));
    setSearch(filter.search || '');
    setSearchInput(filter.search || '');
    setSortBy(filter.sortBy || 'newest');
    setPage(1);
  };

  const deleteSavedFilter = (id) => {
    const updated = savedFilters.filter(f => f.id !== id);
    setSavedFilters(updated);
    sessionStorage.setItem(SAVED_FILTERS_KEY, JSON.stringify(updated));
  };

  // SEO: Set page metadata + JSON-LD structured data
  useEffect(() => {
    document.title = 'Tax Compliance News & Regulatory Updates | Taxera Technologies';
    const setMeta = (name, content) => {
      let el = document.querySelector(`meta[name="${name}"]`) || document.querySelector(`meta[property="${name}"]`);
      if (!el) { el = document.createElement('meta'); el.setAttribute(name.startsWith('og:') ? 'property' : 'name', name); document.head.appendChild(el); }
      el.setAttribute('content', content);
    };
    setMeta('description', 'AI-curated tax compliance news: e-Invoicing mandates, SAF-T, VAT, Pillar Two, digital reporting. Sources: Deloitte, EY, PwC, KPMG, BDO, Crowe, and specialist publications.');
    setMeta('keywords', 'tax compliance news, e-Invoicing, SAF-T, VAT updates, Pillar Two, digital reporting, tax technology, Deloitte tax, EY tax, PwC tax, KPMG tax, BDO tax, Crowe tax');
    setMeta('og:title', 'Tax Compliance News | Taxera Technologies');
    setMeta('og:description', 'AI-curated regulatory developments from Big 4, mid-tier firms, and specialist tax publications.');
    setMeta('og:type', 'website');
  }, []);

  // JSON-LD structured data for SEO + AI/LLM discoverability
  useEffect(() => {
    if (!articles.length) return;
    const jsonLd = {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: 'Tax Compliance & Regulatory News | Taxera Technologies',
      description: 'AI-curated tax compliance news covering eInvoicing mandates, SAF-T reporting, VAT changes, Pillar Two, and digital tax transformation.',
      url: `${window.location.origin}/news`,
      publisher: { '@type': 'Organization', name: 'Taxera Technologies GmbH', url: window.location.origin },
      about: [
        { '@type': 'Thing', name: 'Tax Compliance' },
        { '@type': 'Thing', name: 'Electronic Invoicing' },
        { '@type': 'Thing', name: 'SAF-T Reporting' },
        { '@type': 'Thing', name: 'VAT Compliance' },
      ],
      hasPart: articles.slice(0, 10).map(a => ({
        '@type': 'NewsArticle',
        headline: a.title,
        description: a.summary?.substring(0, 200),
        datePublished: a.created_at,
        author: { '@type': 'Organization', name: a.source_name || 'Taxera Technologies' },
        url: a.source_url || undefined,
        keywords: [...(a.countries || []), ...(a.mandate_types || [])].join(', '),
      })),
    };
    let script = document.getElementById('news-jsonld');
    if (!script) { script = document.createElement('script'); script.id = 'news-jsonld'; script.type = 'application/ld+json'; document.head.appendChild(script); }
    script.textContent = JSON.stringify(jsonLd);
    return () => { const el = document.getElementById('news-jsonld'); if (el) el.remove(); };
  }, [articles]);

  return (
    <PageLayout>
      {/* Hero */}
      <section className="pb-8 pt-4" data-testid="news-hero">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-taxera-teal/10 text-taxera-teal text-sm font-medium rounded-full mb-6">
              <Zap className="w-4 h-4" strokeWidth={1.5} />
              Auto-Updated
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-taxera-charcoal leading-tight mb-5">
              Regulatory news,<br />
              <span className="taxera-gradient-text">as it happens.</span>
            </h1>
            <p className="text-base lg:text-lg text-slate-600 leading-relaxed max-w-2xl">
              AI-curated regulatory developments from across the globe. Sourced from trusted publications, summarized for tax and IT leaders.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Filters */}
      <section className="pb-6" data-testid="news-filters">
        <div className="max-w-7xl mx-auto px-6 space-y-3">

          {/* Compact filter bar — single row */}
          <div className="flex flex-wrap items-center gap-2">
            {/* Region dropdown */}
            <DropdownFilter
              label="Region"
              options={REGION_FILTERS}
              selected={regions}
              onToggle={toggleRegion}
              testPrefix="news-region"
            />

            {/* Country sub-filter — only when regions selected */}
            {availableCountries.length > 0 && (
              <DropdownFilter
                label="Country"
                options={availableCountries}
                selected={countries}
                onToggle={toggleCountry}
                testPrefix="news-country"
              />
            )}

            {/* Topic dropdown */}
            <DropdownFilter
              label="Topic"
              options={MANDATE_FILTERS}
              selected={mandateTypes}
              onToggle={toggleMandate}
              testPrefix="news-topic"
            />

            {/* Source dropdown — dynamic */}
            {stats?.sources?.length > 0 && (
              <DropdownFilter
                label="Source"
                options={stats.sources}
                selected={sources}
                onToggle={toggleSource}
                testPrefix="news-source"
              />
            )}

            {/* Search */}
            <form onSubmit={handleSearch} className="relative flex-1 min-w-[180px] max-w-xs">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search news..."
                className="w-full pl-9 pr-4 py-2 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-taxera-teal focus:ring-1 focus:ring-taxera-teal/20"
                data-testid="news-search-input"
              />
            </form>

            {/* Sort */}
            <div className="relative" ref={sortRef}>
              <button
                onClick={() => setShowSortDropdown(!showSortDropdown)}
                className="flex items-center gap-1.5 px-3 py-2 rounded-xl border border-slate-200 text-xs font-semibold text-slate-600 hover:border-slate-300 transition-colors bg-white whitespace-nowrap"
                data-testid="news-sort-btn"
              >
                <ArrowUpDown className="w-3.5 h-3.5" />
                {SORT_OPTIONS.find(s => s.value === sortBy)?.label}
              </button>
              {showSortDropdown && (
                <div className="absolute top-full right-0 mt-1 w-40 bg-white rounded-xl border border-slate-200 shadow-xl z-30 py-1">
                  {SORT_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() => { setSortBy(opt.value); setShowSortDropdown(false); setPage(1); }}
                      className={`w-full text-left px-3 py-2 text-xs transition-colors ${sortBy === opt.value ? 'bg-taxera-blue/5 text-taxera-blue font-bold' : 'text-slate-600 hover:bg-slate-50'}`}
                      data-testid={`news-sort-${opt.value}`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Active filters bar */}
          {hasActiveFilters && (
            <motion.div
              initial={{ opacity: 0, y: -5 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex flex-wrap items-center gap-2"
              data-testid="news-active-filters"
            >
              <SlidersHorizontal className="w-3.5 h-3.5 text-slate-400 shrink-0" />
              {[...regions].map(r => (
                <FilterChip key={`r-${r}`} label={r} color="blue" onRemove={() => toggleRegion(r)} testId={`active-region-${r.toLowerCase().replace(/\s+/g, '-')}`} />
              ))}
              {[...countries].map(c => (
                <FilterChip key={`c-${c}`} label={c} color="indigo" onRemove={() => toggleCountry(c)} testId={`active-country-${c.toLowerCase().replace(/\s+/g, '-')}`} />
              ))}
              {[...mandateTypes].map(m => (
                <FilterChip key={`m-${m}`} label={m} color="teal" onRemove={() => toggleMandate(m)} testId={`active-mandate-${m.toLowerCase().replace(/\s+/g, '-')}`} />
              ))}
              {[...sources].map(s => (
                <FilterChip key={`s-${s}`} label={s} color="violet" onRemove={() => toggleSource(s)} testId={`active-source-${s.toLowerCase().replace(/\s+/g, '-')}`} />
              ))}
              {search && (
                <FilterChip label={`"${search}"`} color="slate" onRemove={() => { setSearch(''); setSearchInput(''); setPage(1); }} testId="active-filter-search" />
              )}
              <button
                onClick={clearFilters}
                className="px-3 py-1 rounded-full text-xs font-bold text-red-500 hover:bg-red-50 transition-colors"
                data-testid="news-clear-filters"
              >
                Clear all
              </button>
              {stats && (
                <span className="text-xs text-slate-400 ml-auto">{stats.total_articles} articles</span>
              )}
            </motion.div>
          )}
        </div>
      </section>

      {/* Articles */}
      <section className="pb-20" data-testid="news-articles">
        <div className="max-w-7xl mx-auto px-6">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-taxera-blue" />
            </div>
          ) : articles.length === 0 ? (
            <EmptyState hasFilters={hasActiveFilters} onClear={clearFilters} />
          ) : (
            <div className="grid md:grid-cols-2 gap-4">
              {articles.map((article, i) => (
                <NewsCard key={article.hash || i} article={article} index={i} onCountryClick={(c) => { setCountries(toggleSet(countries, c)); setPage(1); }} />
              ))}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-10">
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="p-2 rounded-lg border border-slate-200 text-slate-400 disabled:opacity-30" data-testid="news-prev">
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-sm text-slate-500 px-3">Page {page} of {totalPages}</span>
              <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="p-2 rounded-lg border border-slate-200 text-slate-400 disabled:opacity-30" data-testid="news-next">
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </section>
    </PageLayout>
  );
};

/* ─── Dropdown Filter Component ─── */
const DropdownFilter = ({ label, options, selected, onToggle, testPrefix }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const count = [...selected].filter(s => options.includes(s)).length;

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className={`flex items-center gap-1.5 px-3 py-2 rounded-xl border text-xs font-semibold transition-all whitespace-nowrap ${
          count > 0 ? 'border-taxera-blue/30 bg-taxera-blue/5 text-taxera-blue' : 'border-slate-200 text-slate-600 bg-white hover:border-slate-300'
        }`}
        data-testid={`${testPrefix}-dropdown`}
      >
        {label}
        {count > 0 && <span className="w-4 h-4 rounded-full bg-taxera-blue text-white text-2xs flex items-center justify-center">{count}</span>}
        <ChevronDown className={`w-3 h-3 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute top-full left-0 mt-1 w-52 bg-white rounded-xl border border-slate-200 shadow-xl z-30 py-1 max-h-60 overflow-y-auto">
          {options.map((opt) => (
            <button
              key={opt}
              onClick={() => { onToggle(opt); }}
              className={`w-full text-left px-3 py-2 text-xs transition-colors flex items-center justify-between ${
                selected.has(opt) ? 'bg-taxera-blue/5 text-taxera-blue font-bold' : 'text-slate-600 hover:bg-slate-50'
              }`}
              data-testid={`${testPrefix}-${opt.toLowerCase().replace(/\s+/g, '-')}`}
            >
              {opt}
              {selected.has(opt) && <span className="w-1.5 h-1.5 rounded-full bg-taxera-blue" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

/* ─── Filter Chip with color coding ─── */
const CHIP_COLORS = {
  blue: 'bg-blue-50 text-blue-700',
  indigo: 'bg-indigo-50 text-indigo-700',
  teal: 'bg-teal-50 text-teal-700',
  violet: 'bg-violet-50 text-violet-700',
  slate: 'bg-slate-100 text-slate-700',
};

const FilterChip = ({ label, color = 'slate', onRemove, testId }) => (
  <span
    className={`inline-flex items-center gap-1.5 pl-2.5 pr-1.5 py-1 text-xs font-semibold rounded-full ${CHIP_COLORS[color] || CHIP_COLORS.slate}`}
    data-testid={testId}
  >
    {label}
    <button
      onClick={onRemove}
      className="w-4 h-4 rounded-full hover:bg-black/10 flex items-center justify-center transition-colors"
      data-testid={`${testId}-remove`}
    >
      <X className="w-2.5 h-2.5" />
    </button>
  </span>
);

/* ─── Collapsible News Card ─── */
const NewsCard = ({ article, index, onCountryClick }) => {
  const [expanded, setExpanded] = useState(false);
  const [thumbMissing, setThumbMissing] = useState(false);
  const thumbSrc = newsCardSrc(article);
  const hasTakeaways = article.key_takeaways?.length > 0;
  const summaryPreview = article.summary?.length > 160
    ? article.summary.substring(0, 160) + '...'
    : article.summary;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.03 }}
      className="bg-white rounded-2xl border border-slate-200 hover:border-slate-300 transition-colors duration-200 overflow-hidden"
      data-testid={`news-item-${index}`}
    >
      <div className="p-5 flex gap-5">
        <div className="flex-1 min-w-0">
        {/* Source attribution — prominent at top */}
        <div className="flex items-center justify-between mb-3">
          <a
            href={article.source_url || '#'}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-xs font-bold text-slate-500 hover:text-taxera-blue transition-colors group"
            data-testid={`news-source-${article.hash || ''}`}
          >
            {article.source_home ? (
              <img
                src={`https://www.google.com/s2/favicons?domain=${new URL(article.source_home).hostname}&sz=32`}
                alt=""
                className="w-4 h-4 rounded"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            ) : (
              <ExternalLink className="w-3 h-3" />
            )}
            <span className="group-hover:underline">{article.source_name || 'Source'}</span>
          </a>
          <div className="flex items-center gap-2">
            {article.relevance_score && (
              <span className={`text-2xs font-bold px-1.5 py-0.5 rounded ${
                article.relevance_score >= 8 ? 'bg-emerald-50 text-emerald-600' :
                article.relevance_score >= 6 ? 'bg-amber-50 text-amber-600' :
                'bg-slate-50 text-slate-400'
              }`}>
                {article.relevance_score}/10
              </span>
            )}
            <span className="text-2xs text-slate-400 flex items-center gap-1 whitespace-nowrap">
              <Clock className="w-3 h-3" /> {formatDate(article.created_at)}
            </span>
          </div>
        </div>

        {/* Title */}
        <h3 className="font-heading text-base font-bold text-taxera-charcoal leading-snug mb-2">
          {article.hash ? (
            <Link to={`/news/${article.hash}`} className="hover:text-taxera-blue transition-colors" data-testid={`news-title-link-${index}`}>
              {article.title}
            </Link>
          ) : article.title}
        </h3>

        {/* Summary - collapsible */}
        <p className="text-sm text-slate-600 leading-relaxed mb-3">
          {expanded ? article.summary : summaryPreview}
        </p>

        {/* Key takeaways - only shown when expanded */}
        <AnimatePresence>
          {expanded && hasTakeaways && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
              className="mb-3 overflow-hidden"
            >
              <div className="text-2xs font-bold text-slate-400 uppercase tracking-wide mb-1.5">Key takeaways</div>
              <ul className="space-y-1">
                {article.key_takeaways.map((t, i) => (
                  <li key={`kt-${t.slice(0, 30)}`} className="flex items-start gap-2 text-sm text-slate-500">
                    <span className="shrink-0 w-1 h-1 rounded-full bg-taxera-teal mt-2" />
                    {t}
                  </li>
                ))}
              </ul>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Tags — clickable countries */}
        <div className="flex flex-wrap gap-1.5 mb-3">
          {article.countries?.slice(0, 3).map((c) => (
            <button
              key={c}
              onClick={() => onCountryClick(c)}
              className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 text-blue-600 text-2xs font-semibold rounded-full hover:bg-blue-100 transition-colors"
              data-testid={`news-tag-country-${c.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <MapPin className="w-2.5 h-2.5" /> {c}
            </button>
          ))}
          {article.mandate_types?.slice(0, 2).map((m) => (
            <span key={m} className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-50 text-emerald-600 text-2xs font-semibold rounded-full">
              <Tag className="w-2.5 h-2.5" /> {m}
            </span>
          ))}
        </div>

        {/* Footer: expand toggle */}
        <div className="flex items-center justify-between pt-2 border-t border-slate-100">
          {(article.summary?.length > 160 || hasTakeaways) ? (
            <button
              onClick={() => setExpanded(prev => !prev)}
              className="inline-flex items-center gap-1 text-xs text-taxera-blue font-semibold hover:underline"
              data-testid={`news-toggle-${index}`}
            >
              {expanded ? 'Show less' : 'Read more'}
              <ChevronDown className={`w-3 h-3 transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`} />
            </button>
          ) : <span />}
          {article.source_url && (
            <a
              href={article.source_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-2xs text-slate-400 font-medium hover:text-taxera-blue hover:underline"
            >
              Read original <ExternalLink className="w-3 h-3" />
            </a>
          )}
        </div>
        <div className="mt-2">
          <ShareButtons url={article.source_url || window.location.href} title={article.title} summary={article.summary} compact />
        </div>
        </div>
        {thumbSrc && !thumbMissing && (
          <Link to={`/news/${article.hash}`} className="group/thumb hidden md:block shrink-0 self-center w-[160px] lg:w-[180px]" data-testid={`news-thumb-link-${index}`}>
            <SocialImage
              src={thumbSrc}
              alt={article.title}
              width={180}
              height={180}
              className="w-full aspect-square rounded-[12px] object-cover bg-slate-100 border border-slate-100 transition-[transform,box-shadow] duration-300 ease-out group-hover/thumb:-translate-y-1 group-hover/thumb:scale-[1.03] group-hover/thumb:shadow-xl group-hover/thumb:shadow-slate-900/10"
              testId={`news-thumb-${index}`}
              linked
              onMissing={() => setThumbMissing(true)}
            />
          </Link>
        )}
      </div>
    </motion.div>
  );
};

const EmptyState = ({ hasFilters, onClear }) => (
  <div className="text-center py-20 bg-slate-50 rounded-3xl border border-slate-200" data-testid="news-empty">
    <Newspaper className="w-10 h-10 text-slate-300 mx-auto mb-4" />
    <h3 className="font-heading text-xl font-bold text-taxera-charcoal mb-2">
      {hasFilters ? 'No matching articles' : 'No news yet'}
    </h3>
    <p className="text-sm text-slate-500 mb-3">
      {hasFilters
        ? 'Try adjusting your filters or search query to find what you need.'
        : 'Our automated scanner runs every few hours. Check back soon for the latest regulatory updates.'}
    </p>
    {hasFilters && (
      <button
        onClick={onClear}
        className="px-5 py-2 rounded-xl bg-taxera-blue text-white text-sm font-semibold hover:bg-taxera-blue/90 transition-colors"
        data-testid="news-empty-clear-btn"
      >
        Clear all filters
      </button>
    )}
  </div>
);

export default NewsPage;
